import React from 'react';

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const ALLOWED_HTML_TAGS = new Set([
  'a',
  'b',
  'br',
  'em',
  'i',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'u',
  'ul',
]);

function renderInlineLinks(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // MARKDOWN_LINK_REGEX uses /g and retains lastIndex between calls.
  MARKDOWN_LINK_REGEX.lastIndex = 0;
  while ((match = MARKDOWN_LINK_REGEX.exec(text)) !== null) {
    const [fullMatch, label, href] = match;
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    nodes.push(
      <a key={`${href}-${matchIndex}`} href={href} target="_blank" rel="noopener noreferrer">
        {label}
      </a>,
    );

    lastIndex = matchIndex + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function isSafeHref(href: string): boolean {
  return /^https?:\/\//i.test(href) || (href.startsWith('/') && !href.startsWith('//'));
}

function renderHtmlNodes(nodes: NodeListOf<ChildNode> | ChildNode[], keyPrefix: string): React.ReactNode[] {
  return Array.from(nodes).map((node, index) => renderHtmlNode(node, `${keyPrefix}-${index}`));
}

function renderHtmlNode(node: ChildNode, key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return renderInlineLinks(node.textContent ?? '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'script' || tagName === 'style') {
    return null;
  }

  const children = renderHtmlNodes(element.childNodes, key);

  if (!ALLOWED_HTML_TAGS.has(tagName)) {
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }

  if (tagName === 'a') {
    const href = element.getAttribute('href') ?? '';
    if (!isSafeHref(href)) {
      return <React.Fragment key={key}>{children}</React.Fragment>;
    }

    return (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  return React.createElement(tagName, { key }, children);
}

function renderHtmlContent(content: string): React.ReactNode[] | null {
  if (!HTML_TAG_REGEX.test(content) || typeof DOMParser === 'undefined') {
    return null;
  }

  const document = new DOMParser().parseFromString(content, 'text/html');
  return renderHtmlNodes(document.body.childNodes, 'html');
}

export function renderDisclaimerContent(content: string): React.ReactElement {
  const htmlBlocks = renderHtmlContent(content);
  if (htmlBlocks) {
    return <div className="main-header__disclaimer-content">{htmlBlocks}</div>;
  }

  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={`list-${blocks.length}`}>
        {listItems.map((item, index) => (
          <li key={`item-${index}`}>{renderInlineLinks(item)}</li>
        ))}
      </ul>,
    );
    listItems = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    if (trimmed.startsWith('- ')) {
      listItems.push(trimmed.slice(2));
      return;
    }

    flushList();
    blocks.push(<p key={`p-${blocks.length}`}>{renderInlineLinks(trimmed)}</p>);
  });

  flushList();

  return <div className="main-header__disclaimer-content">{blocks}</div>;
}